import React, { useState } from "react";
import { Alert, MenuItem, Select } from "@mui/material";
import { useTranslation } from "react-i18next";

import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import TabbedDialog from "components/dialogs/TabbedDialog";
import { QueryField, QueryFieldTypes, QueryInfo } from "system/query/QueryInfo";
import { AutoWidgetQueryBehavior, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";

const defaultWidgetRequest = {
  name: 'heatmap',
  endpoint: 'query/records',
  queryParams: {
    flatten: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultHeatmapRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundGeoField = queryInfo.fields?.find((field:QueryField) => field.type === QueryFieldTypes.latitude_longitude) || false;
  if (!foundGeoField) {
    return undefined;
  }

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = {
    query: {
      op: "not",
      values: [{
        field: foundGeoField.id,
        op: "=",
        value: null
      }]
    },
    records: {
      fields: [foundGeoField.id],
      size: 20000
    }
  };

  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export default function HeatmapSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const length = props.queryInfo?.fields.length ?? 0;
  const geohashFieldOptions = [];
  for (let i=0; i<length; i++) {
    const field = props.queryInfo?.fields[i];
    if (field && field.type === QueryFieldTypes.latitude_longitude) {
      geohashFieldOptions.push({
        label: field.label,
        value: field.id
      });
    }
  }

  const [currentTab, setCurrentTab] = useState<number>(0);
  const [geohashField, setGeohashField] = useState<string>(props.widget.data?.geohashField || geohashFieldOptions[0].value || "");

  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setCurrentTab(newValue);

  const updateWidget = () => {
    if (!props.queryInfo) {
      return;
    }
    const foundGeoField = props.queryInfo.fields?.find((field) => field.id === geohashField);
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };

    if (foundGeoField) {
      const requestInitBody = {
        query: {
          op: "not",
          values: [{
            field: foundGeoField.id,
            op: "=",
            value: null
          }]
        },
        records: {
          size: 20000, fields: [foundGeoField.id]
        }
      }
      widgetRequest.requestInit = { body: requestInitBody };
    }

    props.saveWidgetDataAndRequests({
      geohashField: geohashField,
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    })
  }

  return (
    <TabbedDialog
      currentTab={currentTab}
      handleTabChange={handleTabChange}
      maxWidth="xs"
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data")]}
      title={i18n.t("widget-types.heatmap.settings.title")}
    >
      <>
        {currentTab === 0 &&
          <>
            {geohashField &&
              <>
                <SimpleFormLabel>{i18n.t("widget-types.heatmap.settings.field")}</SimpleFormLabel>
                <Select
                  fullWidth
                  onChange={(event) => setGeohashField(event.target.value)}
                  value={geohashField}
                >
                  {geohashFieldOptions.map((option) => {
                    return <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  })}
                </Select>
              </>
            }
            {!geohashField &&
              <Alert severity="warning">{i18n.t("widget-types.heatmap.settings.no-available-fields")}</Alert>
            }
          </>
        }
      </>
    </TabbedDialog>
  )
}